import Backbone from 'backbone'
import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import mockSync from 'scoreboard/components/mock-sync'
import nestCollection from 'scoreboard/components/nest-collection'
import nestModel from 'scoreboard/components/nest-model'
import reorderAfterDrag from 'scoreboard/components/reorder-after-drag'
import ChartDesignerSortSettings from './chart-designer-sort-settings'
import ChartDesignerRange from './chart-designer-range'
import nonAxesPlotType from '../non-axes-plot-type'
import ChartDesignerFeature from './chart-designer-feature'
const { isSerieFieldByIdOrTempId } = ChartDesignerFeature
import DataType from 'scoreboard/datasets/model/data-type'
const { DATE, DATE_TIME, CALENDAR_PERIOD } = DataType
import findByIdsFactory from './find-by-ids-factory'
const findByIds = findByIdsFactory()

const DEFAULT_START_PERIOD_DELTA = -2

/**
 * @see ChartBreakdown.kt
 */
const ChartDesignerBreakdown = Backbone.Model.extend(/** @lends ChartDesignerBreakdown.prototype */{
	initialize: function (attr) {
		this.ranges = new RangeCollection(this.get('ranges'))
		nestCollection({
			parent: this,
			parentAttribute: 'ranges',
			children: this.ranges
		})

		this.sortSettings = new ChartDesignerSortSettings(this.get('sortSettings'))
		nestModel({
			parent: this,
			parentAttribute: 'sortSettings',
			child: this.sortSettings
		})
	},

	deleteRange: function (range) {
		let update = {
			// Decrement the sortOrder on any ranges after the deleted one
			ranges: this.ranges
				.filter(r => r !== range)
				.map((r, i) => {
					let serialized = r.toJSON()
					if (serialized.sortOrder > range.get('sortOrder')) {
						--serialized.sortOrder
					}
					return serialized
				})
		}

		/**
		 * If there is not already a 'non-matching' range defined, create one
		 */
		function ensureNonMatchingRange () {
			if (!_.find(update.ranges, r => r.type === 'non-matching')) {
				update.ranges.push({
					tempId: tempRangeId(),
					type: 'non-matching',
					sortOrder: update.ranges.length,
					active: false
				})
			}
		}

		if (this.get('breakdownBy') === 'every-value') {
			update.breakdownBy = 'specific-values'
			ensureNonMatchingRange()
		} else if (this.get('breakdownBy') === 'every-range') {
			update.breakdownBy = 'specific-ranges'
			ensureNonMatchingRange()
		}

		this.set(update)
	},

	range: function ({ id, tempId }) {
		if (id) {
			return this.ranges.get(id)
		} else {
			return this.ranges.find(r => r.get('tempId') === tempId)
		}
	},

	/**
	 * Applies appropriate sortOrders after changing the order of a range. Also
	 * updates the ChartData so an XHR isn't needed
	 *
	 * @param {Number} newIndex - the new index of the node moved
	 *
	 * @param {Number} prevIndex - the moved node's previous index
	 *
	 * @param {ChartData} data - data for the entire chart; will be mutated to
	 * reflect the new range order
	 */
	reorderRanges: function ({ newIndex, prevIndex, data }) {
		let isFirstBreakdown = this.get('sortOrder') == 0

		// First update the sortOrder for the breakdown ranges
		let reordered = this.ranges.ordered()
		reorderAfterDrag(reordered, newIndex, prevIndex)
		reordered.forEach((reorderedRange, newIndex) => {
			let oldRange = this.range(reorderedRange.pick('id', 'tempId'))
			oldRange.set('sortOrder', newIndex)
		})

		// Note: don't reorder the underlying ranges Backbone.Collection; that will
		// trigger an unnecessary fetch. Everything the cares about range order
		// should be relying on range.sortOrder instead of the array's order

		// To save an XHR, reorder the ranges or plots to reflect the new sortOrders
		if (isFirstBreakdown) {
			// If we're reordering the first breakdown, that is reflected in the
			// array of ranges in each plot breakdown
			_.forEach(data.plots(), plot => {
				plot.breakdown.range = reorderData({
					underlyingRanges: reordered,
					dataArray: plot.breakdown.range,
					idGetter: breakdown => breakdown.underlyingRangeId,
					tempIdGetter: breakdown => breakdown.underlyingRangeTempId
				})
			})
		} else {
			// If we're reordering the second breakdown, that is reflected in
			// the order of the plots themselves (one plot for each of the
			// second breadown's ranges)
			data.plots(reorderData({
				underlyingRanges: reordered,
				dataArray: data.plots(),
				idGetter: plot => plot.breakdown.underlyingRangeId,
				tempIdGetter: plot => plot.breakdown.underlyingRangeTempId
			}))
		}
	},

	getDatasetFieldIdentifier: function ({ dataset, serieId, serieTempId, aggSortOrder = null }) {
		const serieField = _.find(this.get('datasetFields'),f => {
			if (!isSerieFieldByIdOrTempId(f, serieId, serieTempId)) {
				// If the serie IDs don't match, then this definitely isn't
				// the ChartBreakdownDatasetField we're looking for
				return false
			}

			if (_.isNil(aggSortOrder)) {
				// If no aggSortOrder specified, then we can just return the
				// first ChartBreakdownDatasetField we find
				return true
			} else {
				// If we're looking for a specific aggregation's
				// ChartBreakdownDatasetField, we need to check the aggSortOrder
				return f.datasetAggregationSortOrder == aggSortOrder
			}
		})
		if (!serieField) return null

		return dataset.idsToFieldIdentifier({
			fieldId: serieField.datasetFieldId,
			visibleLinkedFieldId: serieField.datasetVisibleLinkedFieldId
		})
	},

	usingSingleFieldBreakdown: function() {
		return _.some(this.get('datasetFields')	, f => !f.serieId && !f.serieTempId)
	},

	setDatasetFieldIdentifier: function ({ fieldIdentifier, dataset, chartDesigner, serieId = null, serieTempId = null, aggSortOrder = null }) {
		let ids = dataset.fieldIdentifierToIds(fieldIdentifier)

		let update = null
		if (dataset && ids) {
			update = {
				serieId,
				serieTempId,
				datasetId: dataset.id,
				datasetFieldId: ids.fieldId,
				datasetVisibleLinkedFieldId: ids.visibleLinkedFieldId,
				datasetAggregationSortOrder: aggSortOrder
			}
		}

		// Used to find or filter out the field in question
		const fieldFilter = f => {
			return isSerieFieldByIdOrTempId(f, serieId, serieTempId)
				// Note: don't use strict ===, because we want undefined and null to match
				&& f.datasetAggregationSortOrder == aggSortOrder
		}

		let datasetFields = this.get('datasetFields') || []
		let updateBreakdownBy = false
		// If we are switching from single field -> multiple fields or vice versa,
		// just clear the current list and add a new field
		if (this.usingSingleFieldBreakdown() != (!serieId && !serieTempId)) {
			datasetFields = update ? [ update ] : []
			updateBreakdownBy = true
		} else if (!update) {
			// Remove the dataset field
			datasetFields = _.filter(datasetFields, fieldFilter)
		} else {
			// Find the field to update
			let toUpdate = _.find(datasetFields, fieldFilter)
			let isNew = false
			if (!toUpdate) {
				// We are inserting a new field
				toUpdate = {
					serieId: update.serieId,
					serieTempId: update.serieTempId
				}
				isNew = true
			}

			if (toUpdate.datasetFieldId != update.datasetFieldId) {
				// If underlying field changed, we need to ensure the breakdown
				// by is still valid
				updateBreakdownBy = true
			}

			toUpdate.datasetId = update.datasetId
			toUpdate.datasetFieldId = update.datasetFieldId
			toUpdate.datasetVisibleLinkedFieldId = update.datasetVisibleLinkedFieldId
			toUpdate.datasetAggregationSortOrder = update.datasetAggregationSortOrder

			if (isNew) {
				datasetFields.push(toUpdate)
			}
		}

		this.set('datasetFields', datasetFields)

		if (serieId === null && update) {
			// If we are using the default single field, update the old fields so
			// anything referencing it is still properly updated
			delete update.serieId
			this.set(update)
		}

		if (!updateBreakdownBy) return

		let oldBreakdownBy = this.get('breakdownBy')
		let defaultBreakdown = ChartDesignerBreakdown.datasetFieldBreakdown({
			chartDesigner: chartDesigner,
			dataset,
			fieldIdentifier,
			serieId,
			serieTempId
		})
		let validBreakdownByValues = ChartDesignerBreakdown.validBreakdownByValues(
			{ dataset, fieldIdentifier})

		if (!_.includes(validBreakdownByValues, oldBreakdownBy)) {
			// The previous breakdownBy isn't valid anymore, so use the default
			// breakdownBy for the new dataset field
			update.breakdownBy = defaultBreakdown.breakdownBy
		}

		// Note that the ranges aren't valid anymore after switching dataset fields,
		// but we don't do anything with them in this dialog. Instead, the call to
		// the /validate-breakdown endpoint when the user clicks Done will take
		// care of generating appropriate ranges.

		this.set(update)
	},

	/**
	 * If this is a breakdown by period ranges OR grouped period ranges
	 */
	isCalendarPeriodBreakdown: function () {
		return this.isCalendarPeriodStandardBreakdown() || this.isCalendarPeriodGroupBreakdown()
	},

	/**
	 * If this is a breakdown by grouped period ranges
	 */
	isCalendarPeriodGroupBreakdown: function() {
		return this.get('breakdownBy') === 'calendar-period-grouped-ranges'
	},

	/**
	 * If this is a breakdown by standard period ranges
	 */
	isCalendarPeriodStandardBreakdown: function() {
		return this.get('breakdownBy') === 'calendar-period-ranges'
	},

	/**
	 * Determines the data type of the breakdown field(s) selected. If there are
	 * a mixture of data types, null is returned. This indicates the user needs
	 * to make some changes
	 */
	fieldDataType: function(chart) {
		if (this.isCalendarPeriodBreakdown()) {
			return DATE
		}

		const datasetFields = this.get('datasetFields')
		if (!datasetFields || !datasetFields.length) return null

		const types = _.map(datasetFields, f => {
			const dataset = chart.data.datasetModel(f.datasetId)
			const { fieldId } = dataset.fieldIdentifierToIds(f.datasetFieldId)
			return dataset.combinedFieldsById[fieldId].dataType
		})

		if (!_.every(types, t => t === types[0])) return null

		return types[0]
	},

	hasError: function () {
		return this.errors().length > 0
	},

	errors () {
		return this.get('error') ? [ this.get('error') ] : []
	},

	/**
	 * Adaptation of Backbone.Model.toJSON() that leaves out attributes that
	 * don't need to be sent to the server, like error messages and valueSuggestions
	 */
	toPostJSON: function () {
		return _.omit(this.toJSON(), 'error', 'valueSuggestions')
	},

	resetToCalendarBreakdown: function() {
		this.set({
			// change to calendar breakdown
			breakdownBy: 'calendar-period-ranges',
			datasetFieldId: null,
			datasetId: null,
			datasetFields: [],
			ranges: [],
			valueSuggestions: null
		})
	}
})

/**
 * Returns a reordered copy of the given array from ChartData to match
 * the new order for the underlying ranges
 */
function reorderData ({ underlyingRanges, dataArray, idGetter, tempIdGetter }) {
	let reorderedData = []
	underlyingRanges
		.filter(r => r.get('type') != 'non-matching' || r.get('active'))
		.forEach(underlyingRange => {
			let dataRange = _.find(dataArray, r => {
				if (!_.isNil(underlyingRange.id)) {
					return idGetter(r) == underlyingRange.id
				} else if (!_.isNil(underlyingRange.get('tempId'))) {
					return tempIdGetter(r) == underlyingRange.get('tempId')
				}
			})

			if (dataRange) {
				reorderedData.push(dataRange)
			}
		})

	return reorderedData
}

/**
 * Returns attributes for a new breakdown on calendar period, using appropriate defaults
 *
 * @returns {Object} attributes to create a new ChartDesignerBreakdown
 */
ChartDesignerBreakdown.calendarPeriodBreakdown = function (chartDesigner) {
	return {
		tempId: tempBreakdownId(),
		sortOrder: nextSortOrder(chartDesigner.breakdowns),
		breakdownBy: 'calendar-period-ranges',
		ranges: [ ChartDesignerBreakdown.calendarPeriodBreakdownRange(
			// #39433 for non-axes charts, default to a single period
			null, nonAxesPlotType(chartDesigner) ? 0 : DEFAULT_START_PERIOD_DELTA) ]
	}
}

/**
 * Returns attributes for a new range for a breakdown on calendar period, using
 * appropriate defaults
 *
 * @param {ChartDesignerBreakdown} [breakdown] - the breakdown the range will
 * be added to, if it exists. Will pick appropriate sortOrder and period ranges
 * adjacent to any existing ranges
 * @param {Number} [startPeriodDelta] - the starting period delta for the new range
 *
 * @returns {Object} attributes to create a new ChartDesignerRange
 */
ChartDesignerBreakdown.calendarPeriodBreakdownRange = function (breakdown, startPeriodDelta = DEFAULT_START_PERIOD_DELTA) {
	let range = {
		tempId: tempRangeId()
	}

	if (breakdown && breakdown.ranges.length > 0) {
		range.sortOrder = nextSortOrder(breakdown.ranges)

		if (breakdown.get('breakdownBy') === 'calendar-period-ranges') {
			applyStandardCalendarRangeDefaults({ breakdown, range })
		} else {
			applyGroupedCalendarRangeDefaults({ breakdown, range })
		}

	} else {
		if (!breakdown || breakdown.get('breakdownBy') === 'calendar-period-ranges') {
			// Note: if the defaults are changed here, they should also be changed in
			// ChartDesignerDefaultBuilder.defaultCalendarPeriodRange()
			_.extend(range, {
				type: 'calendar-period',
				sortOrder: 0,
				startPeriodDelta,
				endPeriodDelta: 0
			})
		} else {
			applyGroupedCalendarRangeDefaults({ breakdown, range })
		}
	}

	return range
}

function applyStandardCalendarRangeDefaults ({ breakdown, range }) {
	range.type = 'calendar-period'

	let relative = !!breakdown.ranges.find(r => !_.isNil(r.get('startPeriodDelta')))

	// Default to periods (relative or absolute) that are immediately before
	// the oldest existing range. So if there's a range for "2 Periods Before
	// to Current Period", this will create a range of "5 periods before to
	// 3 periods before"
	if (relative) {
		let minRange = _.minBy(breakdown.ranges.toArray(), r => r.get('startPeriodDelta'))
		range.calendarId = minRange.get('calendarId')
		range.endPeriodDelta = minRange.get('startPeriodDelta') - 1
		range.startPeriodDelta = range.endPeriodDelta - 2
	} else {
		let oldestRange = _.minBy(breakdown.ranges.toArray(), r => {
			let startPeriod = calendars.periodsById[r.get('startPeriodId')]
			return startPeriod.start
		})

		range.calendarId = oldestRange.get('calendarId')

		// End with the period right before the oldest period, then go back
		// to more to get to the start. Note that safeRelativePeriod will
		// just give us the oldest periods possible if that would take us
		// out of the bounds of the calendar
		range.endPeriodId = calendars.safeRelativePeriod(
			oldestRange.get('startPeriodId'), -1).id
		range.startPeriodId = calendars.safeRelativePeriod(
			range.endPeriodId, -2).id
	}
}

function applyGroupedCalendarRangeDefaults ({ breakdown, range }) {
	range.type = 'calendar-period-grouped'
	range.sortOrder = range.sortOrder ?? 0

	// Unlike "standard" calendar period breakdown ranges, we can't just
	// "shift back" to earlier periods. We really don't know what they would
	// want to do next, so just default to the last range in the list and
	// let them work from there
	let sampleRange = _(breakdown.ranges.toArray())
		.filter(r => r.get('type') === 'calendar-period-grouped')
		.last()

	let sampleRangeAttrs
	if (sampleRange) {
		sampleRangeAttrs = sampleRange.toJSON()
	} else {
		let groupAndBreakdown = groupAndBreakdownCalendars()

		sampleRangeAttrs = {
			groupCalendarId: groupAndBreakdown.groupCalendar.id,
			breakdownCalendarId: groupAndBreakdown.breakdownCalendar.id,
			startPeriodIndex: 0,
			endPeriodIndex: groupAndBreakdown.breakdownPeriodCount - 1
		}
	}

	_.extend(range, _.pick(sampleRangeAttrs, 'groupCalendarId', 'breakdownCalendarId',
		'startPeriodIndex', 'endPeriodIndex', 'startPeriodDelta', 'endPeriodDelta'))
}

ChartDesignerBreakdown.datasetFieldBreakdown = function ({ chartDesigner, dataset, fieldIdentifier, serieId = null, serieTempId = null }) {
	let { fieldId, visibleLinkedFieldId } = dataset.fieldIdentifierToIds(fieldIdentifier)

	let field = dataset.combinedFieldsById[fieldId]

	let breakdown
	if (ChartDesignerBreakdown.isCalendarPeriodBreakdownType(field.dataType)) {
		breakdown = ChartDesignerBreakdown.calendarPeriodBreakdown(chartDesigner)
	} else {
		breakdown = {
			breakdownBy: _.isEmpty(field.ranges) ? 'every-value' : 'every-range',
			ranges: []
		}
	}

	_.extend(breakdown, ChartDesignerBreakdown.basicDatasetBreakdown({ dataset, chartDesigner }), {
		datasetFields: [{
			datasetId: dataset.get('id'),
			datasetFieldId: fieldId,
			datasetVisibleLinkedFieldId: visibleLinkedFieldId,
			serieId,
			serieTempId
		}]
	})

	// Just let BreakdownRangeAdjuster.kt handle creating default ranges, so we
	// don't have to duplicate some of the more complex logic here
	breakdown.ranges = []

	return breakdown
}

ChartDesignerBreakdown.isCalendarPeriodBreakdownType = function (dataType) {
	return _.includes([ DATE, DATE_TIME, CALENDAR_PERIOD ], dataType)
}

ChartDesignerBreakdown.basicDatasetBreakdown = function ({ dataset, chartDesigner }) {
	return {
		tempId: tempBreakdownId(),
		datasetId: _.get(dataset, 'id'),
		sortOrder: nextSortOrder(chartDesigner.breakdowns)
	}
}

/**
 * Returns a new breakdown range for a given dataset value
 */
ChartDesignerBreakdown.datasetValueBreakdownRange = function ({ breakdown, value }) {
	return new ChartDesignerRange({
		tempId: tempRangeId(),
		type: 'dataset-value',
		value,
		sortOrder: nextSortOrder(breakdown.ranges)
	})
}

/**
 * Returns a new breakdown range for a given dataset range
 */
ChartDesignerBreakdown.datasetRangeBreakdownRange = function ({ breakdown, datasetRangeId, datasetRangeType }) {
	return new ChartDesignerRange({
		tempId: tempRangeId(),
		type: 'dataset-range',
		datasetRangeId,
		datasetRangeType,
		sortOrder: nextSortOrder(breakdown.ranges)
	})
}

ChartDesignerBreakdown.validBreakdownByDataType = function(dataType, hasRanges = false) {
	if (ChartDesignerBreakdown.isCalendarPeriodBreakdownType(dataType)) {
		return [ 'calendar-period-ranges', 'calendar-period-grouped-ranges' ]
	} else {
		let valid = [ 'every-value', 'specific-values' ]
		if (hasRanges) {
			valid.push('every-range', 'specific-ranges')
		}
		return valid
	}
}

ChartDesignerBreakdown.validBreakdownByValues = function ({ dataset, fieldIdentifier }) {
	let field = dataset.getFieldByFieldIdentifier(fieldIdentifier)
	return ChartDesignerBreakdown.validBreakdownByDataType(field.dataType, !!field.ranges)
}

function tempBreakdownId () {
	return _.uniqueId('b')
}

function tempRangeId () {
	return _.uniqueId('r')
}

function nextSortOrder (collection) {
	let maxSortOrder = _.max(collection.map(o => o.get('sortOrder')))
	return _.isNil(maxSortOrder) ? 0 : maxSortOrder + 1
}

/**
 * Returns an appropriate group calendar, breakdown calendar, and the number
 * of breakdown calendar periods in the group calendar period. Prefers to use
 * the given default calendar as the breakdown calendar.
 */
function groupAndBreakdownCalendars () {
	let defaultCalendar = calendars.calendars[0]

	let largerCalendar = largerOrSmallerCalendar(defaultCalendar, true)

	let groupCal, breakdownCal
	if (largerCalendar != null) {
		// If there's a larger calendar, then we can use the default calendar
		// as the breakdown calendar
		breakdownCal = defaultCalendar
		groupCal = largerCalendar
	} else {
		// If there isn't a larger calendar, then try to use the default calendar
		// as the group calendar and find a smaller calendar to use for the
		// breakdown calendar
		let smallerCalendar = largerOrSmallerCalendar(defaultCalendar, false)
		if (smallerCalendar == null) {
			throw new Error('Unable to find a calendar larger or smaller than '
				+ defaultCalendar.label)
		} else {
			breakdownCal = smallerCalendar
			groupCal = defaultCalendar
		}
	}

	let groupPeriodId = calendars.defaultPeriods[groupCal.id]
	let groupPeriod = calendars.periodsById[groupPeriodId]
	let breakdownPeriods = breakdownPeriodsInGroupPeriod({ breakdownCal, groupPeriod })

	return {
		groupCalendar: groupCal,
		breakdownCalendar: breakdownCal,
		breakdownPeriodCount: breakdownPeriods.length
	}
}

function breakdownPeriodsInGroupPeriod ({ breakdownCal, groupPeriod }) {
	return breakdownCal.periods.filter(breakdownPeriod => {
		return breakdownPeriod.end.isSameOrAfter(groupPeriod.start)
			&& breakdownPeriod.end.isSameOrBefore(groupPeriod.end)
	})
}

function largerOrSmallerCalendar (cal, larger) {
	return _(calendars.calendars)
		.filter(other => {
			if (larger) {
				return cal.avgPeriodSize < other.avgPeriodSize
			} else {
				return cal.avgPeriodSize > other.avgPeriodSize
			}
		})
		.sortBy(c => {
			if (larger) {
				return c.avgPeriodSize
			} else {
				return -1 * c.avgPeriodSize
			}
		})
		.head()
}

const RangeCollection = Backbone.Collection.extend({
	sync: mockSync,
	model: ChartDesignerRange,

	/**
	 * Given either a "real" persisted ID or a tempId for a range that hasn't
	 * yet been saved, returns the corresponding ChartDesignerRange model
	 */
	findByIds: function ({ id, tempId }) {
		return findByIds({ id, tempId, collection: this })
	},

	/**
	 * Returns the ranges, sorted by sortOrder
	 *
	 * @returns {ChartDesignerRange[]}
	 */
	ordered: function () {
		return _.sortBy(this.toArray(), r => r.get('sortOrder'))
	}
})

export default ChartDesignerBreakdown
export { tempBreakdownId }
export { tempRangeId }
export { DEFAULT_START_PERIOD_DELTA }
ChartDesignerBreakdown.__filename__ = 'charts/model/chart-designer-breakdown.js'
